import React, { useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import FormValidationSummary, { mergeValidationErrors, validationMessage } from '../../../Shared/Components/FormValidationSummary'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'
import Nav from 'react-bootstrap/Nav'
import Tab from 'react-bootstrap/Tab'
import axios from 'axios'

const Reqd = () => <span className="text-danger">*</span>

function FieldLabel({ children, required = true }) {
    return (
        <Form.Label className="fw-bold text-gray-800">
            {children}:{required ? ' ' : ''}
            {required && <Reqd />}
        </Form.Label>
    )
}

function formatDate(val) {
    if (!val) return ''
    return String(val).slice(0, 10)
}

function formatBeneficiariesLines(beneficiaries) {
    return (beneficiaries || [])
        .map((b) => [b.name, b.relationship, b.phone].filter(Boolean).join(' '))
        .filter(Boolean)
        .join('\n')
}

function initialBeneficiariesText(entry) {
    if (entry?.beneficiaries_text) {
        return entry.beneficiaries_text
    }

    if (entry?.beneficiaries_snapshot?.length) {
        return formatBeneficiariesLines(entry.beneficiaries_snapshot)
    }

    return ''
}

const ClaimRecordForm = ({ entry, offices, advisers, statuses, benefits, testimonials }) => {
    const { flash, errors: pageErrors } = usePage().props
    const isEdit = Boolean(entry)
    const [activeTab, setActiveTab] = useState('asegurado')
    const [policyLoading, setPolicyLoading] = useState(false)
    const [policyError, setPolicyError] = useState('')
    const [policyFound, setPolicyFound] = useState(Boolean(entry?.policy_number))
    const [applicationLinked, setApplicationLinked] = useState(null)

    const form = useForm({
        policy_number: entry?.policy_number || '',
        insured_name: entry?.insured_name || '',
        insured_phone: entry?.insured_phone || '',
        insured_email: entry?.insured_email || '',
        beneficiary_phone: entry?.beneficiary_phone || '',
        beneficiary_address: entry?.beneficiary_address || '',
        beneficiaries_text: initialBeneficiariesText(entry),
        beneficiaries_snapshot: entry?.beneficiaries_snapshot || [],
        requesting_office_id: entry?.requesting_office_id ? String(entry.requesting_office_id) : '',
        processing_office_id: entry?.processing_office_id ? String(entry.processing_office_id) : '',
        claim_benefit_catalog_id: entry?.claim_benefit_catalog_id ? String(entry.claim_benefit_catalog_id) : '',
        claim_status_catalog_id: entry?.claim_status_catalog_id ? String(entry.claim_status_catalog_id) : '',
        processing_adviser_id: entry?.processing_adviser_id ? String(entry.processing_adviser_id) : '',
        office_reception_date: formatDate(entry?.office_reception_date),
        inbursa_entry_date: formatDate(entry?.inbursa_entry_date),
        inbursa_executive: entry?.inbursa_executive || '',
        claim_folio: entry?.claim_folio || '',
        tys_folio: entry?.tys_folio || '',
        follow_up_notes: entry?.follow_up_notes || '',
        cct: entry?.cct || '',
        cct_name: entry?.cct_name || '',
        cct_level: entry?.cct_level || '',
        cct_municipality: entry?.cct_municipality || '',
        cct_zone: entry?.cct_zone || '',
        settlement_sign_date: formatDate(entry?.settlement_sign_date),
        payment_discharge_date: formatDate(entry?.payment_discharge_date),
        payment_amount: entry?.payment_amount ?? '',
        video_authorized: entry?.video_authorized ?? null,
        video_date: formatDate(entry?.video_date),
        claim_testimonial_id: entry?.claim_testimonial_id ? String(entry.claim_testimonial_id) : '',
    })

    const allErrors = mergeValidationErrors(form.errors, pageErrors)
    const fieldError = (key) => validationMessage(allErrors, key)
    const invalidClass = (key) => (fieldError(key) ? 'is-invalid' : '')
    const FieldFeedback = ({ name }) =>
        fieldError(name) ? <div className="invalid-feedback d-block">{fieldError(name)}</div> : null

    const emptyLookupFields = {
        insured_name: '',
        insured_phone: '',
        insured_email: '',
        beneficiary_phone: '',
        beneficiary_address: '',
        beneficiaries_text: '',
        beneficiaries_snapshot: [],
        cct: '',
        cct_name: '',
        cct_level: '',
        cct_municipality: '',
        cct_zone: '',
    }

    const lookupPolicy = async () => {
        const num = form.data.policy_number.trim()
        if (!num) {
            setPolicyError('Ingrese el número de póliza.')
            return
        }
        setPolicyLoading(true)
        setPolicyError('')
        try {
            const { data } = await axios.get(route('claims.records.lookup-policy'), { params: { policy_number: num } })
            if (!data.found) {
                setPolicyError(data.message || 'Póliza no encontrada.')
                setPolicyFound(false)
                setApplicationLinked(null)
                form.setData((d) => ({ ...d, ...emptyLookupFields }))
                return
            }
            const workplace = data.workplace || {}
            setPolicyFound(true)
            setApplicationLinked(data.application_linked ?? true)
            form.setData((d) => ({
                ...d,
                insured_name: data.insured?.name || '',
                insured_phone: data.insured?.phone || '',
                insured_email: data.insured?.email || '',
                beneficiaries_text: data.beneficiaries_text || formatBeneficiariesLines(data.beneficiaries),
                beneficiaries_snapshot: data.beneficiaries || [],
                cct: workplace.cct || '',
                cct_name: workplace.cct_name || '',
                cct_level: workplace.cct_level || '',
                cct_municipality: workplace.cct_municipality || '',
                cct_zone: workplace.cct_zone || '',
            }))
            setActiveTab('asegurado')
        } catch {
            setPolicyError('No se pudo consultar la póliza.')
        } finally {
            setPolicyLoading(false)
        }
    }

    const submit = (e) => {
        e.preventDefault()
        form.transform((data) => ({
            ...data,
            video_authorized: data.video_authorized === '' || data.video_authorized === null
                ? null
                : data.video_authorized === true || data.video_authorized === '1' || data.video_authorized === 1,
            payment_amount: data.payment_amount === '' ? null : Number(data.payment_amount),
        }))
        if (isEdit) {
            form.put(route('claims.records.update', entry.id), { preserveScroll: true })
        } else {
            form.post(route('claims.records.store'), { preserveScroll: true })
        }
    }

    const selectField = (label, field, options, labelKey = 'name', required = true) => (
        <div className="mb-4">
            <FieldLabel required={required}>{label}</FieldLabel>
            <Form.Select value={form.data[field]} onChange={(e) => form.setData(field, e.target.value)} className={invalidClass(field)}>
                <option value="">Seleccione...</option>
                {(options || []).map((o) => (
                    <option key={o.id} value={o.id}>{o[labelKey] || o.display_name || o.nombre}</option>
                ))}
            </Form.Select>
            <FieldFeedback name={field} />
        </div>
    )

    const textField = (label, field, placeholder = '', type = 'text', required = true, rows = null) => (
        <div className="mb-4">
            <FieldLabel required={required}>{label}</FieldLabel>
            <Form.Control
                type={type}
                as={rows ? 'textarea' : undefined}
                rows={rows || undefined}
                placeholder={placeholder}
                value={form.data[field]}
                onChange={(e) => form.setData(field, e.target.value)}
                className={invalidClass(field)}
            />
            <FieldFeedback name={field} />
        </div>
    )

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            <FormValidationSummary errors={allErrors} />
            <Col xs={12}>
                <Breadcrumbs currentPath={isEdit ? `admin/claims/records/${entry.id}/edit` : 'admin/claims/records/create'} />
            </Col>
            <Col xs={12}>
                <Card>
                    <Card.Header className="border-0 pt-6">
                        <Card.Title className="fw-bold fs-3">FORMULARIO DE REGISTRO</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={submit}>
                            <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
                                <Nav variant="tabs" className="mb-4 flex-wrap">
                                    <Nav.Item><Nav.Link eventKey="asegurado">Datos del asegurado</Nav.Link></Nav.Item>
                                    <Nav.Item><Nav.Link eventKey="general">Datos generales</Nav.Link></Nav.Item>
                                    <Nav.Item><Nav.Link eventKey="siniestro">Datos del siniestro</Nav.Link></Nav.Item>
                                    <Nav.Item><Nav.Link eventKey="centro">Datos del centro de trabajo</Nav.Link></Nav.Item>
                                    <Nav.Item><Nav.Link eventKey="pago">Datos del pago</Nav.Link></Nav.Item>
                                    <Nav.Item><Nav.Link eventKey="video">Datos del video</Nav.Link></Nav.Item>
                                </Nav>
                                <Tab.Content>
                                    <Tab.Pane eventKey="asegurado">
                                        <Row className="mb-2">
                                            <Col md={6}>
                                                <div className="mb-4">
                                                    <FieldLabel>Número de póliza</FieldLabel>
                                                    <div className="d-flex gap-2">
                                                        <Form.Control
                                                            value={form.data.policy_number}
                                                            onChange={(e) => form.setData('policy_number', e.target.value)}
                                                            className={invalidClass('policy_number')}
                                                            placeholder="Ingrese número de póliza"
                                                        />
                                                        <Button type="button" variant="secondary" onClick={lookupPolicy} disabled={policyLoading}>
                                                            {policyLoading ? '...' : 'Buscar'}
                                                        </Button>
                                                    </div>
                                                    {policyError && <div className="text-danger fs-8 mt-1">{policyError}</div>}
                                                    <FieldFeedback name="policy_number" />
                                                    <Form.Text className="text-muted">
                                                        Busque la póliza para precargar los datos del asegurado y del centro de trabajo.
                                                    </Form.Text>
                                                </div>
                                            </Col>
                                        </Row>
                                        {policyFound && applicationLinked === false && (
                                            <Alert variant="warning" className="py-2 px-3 fs-8 mb-4 text-gray-800 border border-warning">
                                                Póliza encontrada sin solicitud ligada. Los datos del asegurado pueden editarse manualmente.
                                            </Alert>
                                        )}
                                        {policyFound && applicationLinked === true && (
                                            <Alert variant="success" className="py-2 px-3 fs-8 mb-4 text-gray-800 border border-success">
                                                Datos precargados desde la solicitud ligada a la póliza. Puede modificarlos si es necesario.
                                            </Alert>
                                        )}
                                        {!policyFound && !isEdit && (
                                            <Alert variant="info" className="py-2 px-3 fs-8 mb-4 text-gray-800 border border-info">
                                                Ingrese el número de póliza y pulse «Buscar» para cargar la información del asegurado.
                                            </Alert>
                                        )}
                                        <Row>
                                            <Col md={6}>
                                                {textField('Nombre del asegurado', 'insured_name', 'INGRESA EL NOMBRE DEL ASEGURADO')}
                                                {textField('Teléfono del asegurado', 'insured_phone', 'INGRESA EL TELÉFONO DEL ASEGURADO')}
                                                {textField('Correo electrónico del asegurado', 'insured_email', 'INGRESA EL CORREO DEL ASEGURADO', 'email')}
                                                {textField('Teléfono del beneficiario', 'beneficiary_phone', 'INGRESA EL TELÉFONO DEL BENEFICIARIO')}
                                            </Col>
                                            <Col md={6}>
                                                {textField('Beneficiarios', 'beneficiaries_text', 'Ingrese a los beneficiarios', 'text', true, 6)}
                                                {textField('Dirección del beneficiario', 'beneficiary_address', 'Ingrese la dirección del beneficiario', 'text', true, 4)}
                                            </Col>
                                        </Row>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="general">
                                        <Row>
                                            <Col md={6}>
                                                {selectField('Oficina que solicita el siniestro', 'requesting_office_id', offices, 'nombre')}
                                                {selectField('Oficina que tramita el siniestro', 'processing_office_id', offices, 'nombre')}
                                            </Col>
                                            <Col md={6}>
                                                {selectField('Beneficio reclamado', 'claim_benefit_catalog_id', benefits)}
                                                {selectField('Estatus del siniestro', 'claim_status_catalog_id', statuses)}
                                                {selectField('Asesor ó ejecutiva que tramita el siniestro', 'processing_adviser_id', advisers, 'display_name')}
                                            </Col>
                                        </Row>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="siniestro">
                                        <Row>
                                            <Col md={6}>
                                                {textField('Fecha de recepción en oficina', 'office_reception_date', '', 'date')}
                                                {textField('Fecha de ingreso a INBURSA', 'inbursa_entry_date', '', 'date')}
                                                {textField('Folio de siniestro', 'claim_folio', 'Ingrese folio', 'text', false)}
                                                {textField('Folio TYS', 'tys_folio', 'Ingrese folio TYS', 'text', false)}
                                            </Col>
                                            <Col md={6}>
                                                {textField('Ejecutivo de INBURSA', 'inbursa_executive', 'Nombre del ejecutivo')}
                                                {textField('Seguimiento', 'follow_up_notes', 'Notas de seguimiento', 'text', false, 4)}
                                            </Col>
                                        </Row>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="centro">
                                        <Row>
                                            <Col md={6}>
                                                {textField('CCT', 'cct', 'Ingrese CCT', 'text', false)}
                                                {textField('Nombre del CCT', 'cct_name', 'Ingrese nombre', 'text', false)}
                                                {textField('Nivel del CCT', 'cct_level', 'Ingrese nivel', 'text', false)}
                                            </Col>
                                            <Col md={6}>
                                                {textField('Municipio del CCT', 'cct_municipality', 'Ingrese municipio', 'text', false)}
                                                {textField('Zona', 'cct_zone', 'Ingrese zona', 'text', false)}
                                            </Col>
                                        </Row>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="pago">
                                        <Row>
                                            <Col md={6}>
                                                {textField('Fecha de firma de finiquito', 'settlement_sign_date', '', 'date', false)}
                                                {textField('Fecha de egreso (pago)', 'payment_discharge_date', '', 'date', false)}
                                            </Col>
                                            <Col md={6}>
                                                {textField('Importe de pago', 'payment_amount', '0.00', 'number', false)}
                                            </Col>
                                        </Row>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="video">
                                        <Row>
                                            <Col md={6}>
                                                <div className="mb-4">
                                                    <Form.Label className="fw-bold">¿Autorizó video?</Form.Label>
                                                    <div className="d-flex gap-4">
                                                        <Form.Check type="radio" name="video_authorized" label="SI" checked={form.data.video_authorized === true} onChange={() => form.setData('video_authorized', true)} />
                                                        <Form.Check type="radio" name="video_authorized" label="NO" checked={form.data.video_authorized === false} onChange={() => form.setData('video_authorized', false)} />
                                                    </div>
                                                </div>
                                                {textField('Fecha de video', 'video_date', '', 'date', false)}
                                            </Col>
                                            <Col md={6}>
                                                {selectField('Nombre del video (testimonio)', 'claim_testimonial_id', testimonials, 'insured_name', false)}
                                            </Col>
                                        </Row>
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                            <div className="d-flex flex-column align-items-center gap-3 mt-6">
                                <Button type="submit" variant="primary" disabled={form.processing}>
                                    {isEdit ? 'Actualizar' : 'Registrar'}
                                </Button>
                                <Link href={route('claims.records.index')} className="btn btn-light">Regresar</Link>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    )
}

ClaimRecordForm.layout = (page) => <Layout title="Registro de siniestro">{page}</Layout>

export default ClaimRecordForm
